<template>
  <LoaderWrapper ref="pageLoader" class="h-100">
    <div class="row m-4 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">

          <div class="row">
            <div class="col-sm-12">
              <div class="overflow-auto" style="min-height: 100vh">
                <table class="table table-striped border-light align-middle text-left text-nowrap position-relative">
                  <thead>
                  <tr>
                    <th class="text-capitalize-first">{{ helper.trans('labels.source') }} A</th>
                    <th class="text-capitalize-first">{{ helper.trans('labels.rate') }}</th>
                    <th class="text-capitalize-first">{{ helper.trans('labels.source') }} B</th>
                    <th class="text-capitalize-first">{{ helper.trans('labels.rate') }}</th>
                    <th class="text-capitalize-first">{{ helper.trans('labels.diffSum') }}</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, index) in compareList" :key="index">
                    <td>{{ helper.trans(`rate_sources.${item.rateA.source}`) }} {{item.rateA.toCurrency}}/{{item.rateA.fromCurrency}}</td>
                    <td>{{numberFormatter.formatNumber(getRate(item.rateA), 6) }} {{item.rateA.fromCurrency}}</td>
                    <td>{{ helper.trans(`rate_sources.${item.rateB.source}`) }} {{item.rateB.toCurrency}}/{{item.rateB.fromCurrency}}</td>
                    <td>{{numberFormatter.formatNumber(getRate(item.rateB), 6) }} {{item.rateB.fromCurrency}}</td>
                    <td>
                      {{numberFormatter.formatNumber(getDiff(item.rateA, item.rateB), 6) }} {{item.rateA.fromCurrency}} ({{numberFormatter.formatNumber(getDiffPercent(item.rateA, item.rateB), 2) }}%)
                    </td>
                    <td class="text-end"><button @click.prevent="remove(index)" class="btn btn-outline-danger"><i class="fa fa-trash"></i></button></td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <Multiselect
                                   class=""
                                   v-model="form.sourceA"
                                   :options="currencies"
                                   :custom-label="(opt) => helper.trans(`rate_sources.${opt.source}`) + ` ${opt.toCurrency}/${opt.fromCurrency}`"
                                   select-label="Select"
                                   placeholder=""
                                   open-direction="bottom"
                                   :multiple="false"
                                   :searchable="true"
                                   :internal-search="true"
                                   :clear-on-select="false"
                                   :close-on-select="true"
                                   :options-limit="100"
                                   :limit="1000"
                                   :show-no-results="false"
                                   :hide-selected="false"
                      >
                      </Multiselect>
                    </td>
                    <td colspan="2">
                      <Multiselect
                        class=""
                        v-model="form.sourceB"
                        :options="currencies"
                        :custom-label="(opt) => helper.trans(`rate_sources.${opt.source}`) + ` ${opt.fromCurrency}/${opt.toCurrency}`"
                        select-label="Select"
                        placeholder=""
                        open-direction="bottom"
                        :multiple="false"
                        :searchable="true"
                        :internal-search="true"
                        :clear-on-select="false"
                        :close-on-select="true"
                        :options-limit="100"
                        :limit="1000"
                        :show-no-results="false"
                        :hide-selected="false"
                      >
                      </Multiselect>
                    </td>
                    <td></td>
                    <td class="text-end"><button class="btn btn-outline-primary text-capitalize-first" @click.prevent="add">{{ helper.trans('button.add') }}</button></td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </LoaderWrapper>
</template>

<script lang="ts">
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {computed, defineComponent, onMounted, reactive, ref, toRefs} from 'vue'
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {api} from "@/service/APIClient";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import CurrencyRateDTO from "@/models/CurrencyRateDTO";
import Multiselect from "vue-multiselect";
import CurrencyCompareItemDTO from "@/models/CurrencyCompareItemDTO";
import {Dictionary} from "@/types";

export default defineComponent({
  components: {
    LoaderWrapper,
    Multiselect
  },
  setup() {
    const store = useStore();
    const pageLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      form: {
        sourceA: null as CurrencyRateDTO|null,
        sourceB: null as CurrencyRateDTO|null,
      },
      currencies: [] as CurrencyRateDTO[],
      rates: {} as Dictionary<string>
    });

    const loadCurrencies= async () => {
      pageLoader.value?.show()
      try {
        state.currencies = await api.currencyRatesAll()
        for (const currency of state.currencies) {
          state.rates[`${currency.source}_${currency.fromCurrency}_${currency.toCurrency}`] = currency.rate
        }
      } finally {
        pageLoader.value?.hide()
      }
    }

    const add = async () => {
      if (!state.form.sourceA || !state.form.sourceB) {
        return;
      }

      if (
        state.form.sourceA.fromCurrency !== state.form.sourceB.fromCurrency
        || state.form.sourceA.toCurrency !== state.form.sourceB.toCurrency
      ) {
        return;
      }

      const dto = {
        rateA: state.form.sourceA,
        rateB: state.form.sourceB,
      } as CurrencyCompareItemDTO
      store.dispatch('rateSources/addToCompareList', dto)

      state.form.sourceA = null;
      state.form.sourceB = null;
    }

    const remove = async (index: number) => {
      store.dispatch('rateSources/removeFromCompareList', index)
    }

    const getRate = (item: CurrencyRateDTO) => {
      return state.rates[`${item.source}_${item.fromCurrency}_${item.toCurrency}`] ?? null
    }

    const getDiff = (itemA: CurrencyRateDTO, itemB: CurrencyRateDTO) => {
      const rateA = state.rates[`${itemA.source}_${itemA.fromCurrency}_${itemA.toCurrency}`] ?? null
      const rateB = state.rates[`${itemB.source}_${itemB.fromCurrency}_${itemB.toCurrency}`] ?? null

      return (parseFloat(rateB) - parseFloat(rateA));
    }

    const getDiffPercent = (itemA: CurrencyRateDTO, itemB: CurrencyRateDTO) => {
      const rateA = state.rates[`${itemA.source}_${itemA.fromCurrency}_${itemA.toCurrency}`] ?? null
      const rateB = state.rates[`${itemB.source}_${itemB.fromCurrency}_${itemB.toCurrency}`] ?? null

      return (parseFloat(rateB) - parseFloat(rateA))/parseFloat(rateB) * 100;
    }

    onMounted(async () => {
      await loadCurrencies();
    })

    return {
      ...toRefs(state),
      pageLoader,
      dateHelper,
      numberFormatter,
      helper,
      compareList: computed(() => store.getters["rateSources/compareList"]),
      loadCurrencies,
      getDiff,
      getDiffPercent,
      add,
      getRate,
      remove
    }
  }
})
</script> 